import fileDownload from "js-file-download";
import { format } from "date-fns";
import { CHART_INK } from "./chartPalette";

// Chart downloads.
//
// These used to screenshot the page with html2canvas and save the JPEG under a
// .png name, so a download carried whatever happened to be on screen around
// the chart, at screen resolution, and the pie chart could not be downloaded
// at all. Now an image is composed from the chart's own SVG at 2x, with its
// title, the date range and a legend drawn underneath - a picture that stands
// on its own in a slide or an email. Every chart can also hand over its
// numbers as a spreadsheet (utils/spreadsheet.js).

const FONT = 'Roboto, system-ui, -apple-system, "Segoe UI", sans-serif';
const PAD = 24;
const SWATCH = 12;
const LEGEND_ROW = 22;
const LEGEND_GAP = 20;

const today = () => format(new Date(), "yyyy-MM-dd");

/** "Species abundance" -> "Species-abundance-2026-10-08.png" */
export const exportFilename = (title, extension) =>
  `${String(title || "chart").trim().replace(/\s+/g, "-")}-${today()}.${extension}`;

/**
 * Lays legend items out in rows that fit `maxWidth`, measuring each label with
 * `measure(text) -> px`. Returns [{label, color, x, row}] and the row count.
 */
export const layoutLegend = (items, maxWidth, measure) => {
  const placed = [];
  let x = 0;
  let row = 0;
  items.forEach(({ label, color }) => {
    const width = SWATCH + 6 + measure(label);
    if (x > 0 && x + width > maxWidth) {
      row += 1;
      x = 0;
    }
    placed.push({ label, color, x, row });
    x += width + LEGEND_GAP;
  });
  return { placed, rows: items.length ? row + 1 : 0 };
};

// The chart's plot: the largest SVG in the container. Recharts also draws
// small SVGs for legend icons, which are not the chart.
const largestSvg = (container) =>
  [...(container?.querySelectorAll("svg") ?? [])].reduce((best, svg) => {
    const { width, height } = svg.getBoundingClientRect();
    return !best || width * height > best.area ? { svg, width, height, area: width * height } : best;
  }, null);

// Text in a Recharts SVG takes its font from CSS on the page, which a
// serialized SVG loses; copy the computed values onto each text element.
const inlineTextStyles = (source, clone) => {
  const sourceText = source.querySelectorAll("text, tspan");
  const cloneText = clone.querySelectorAll("text, tspan");
  sourceText.forEach((node, i) => {
    const style = window.getComputedStyle(node);
    const target = cloneText[i];
    if (!target) return;
    target.setAttribute("font-family", style.fontFamily || FONT);
    target.setAttribute("font-size", style.fontSize);
    target.setAttribute("font-weight", style.fontWeight);
    if (!target.getAttribute("fill")) target.setAttribute("fill", style.fill || CHART_INK.secondary);
  });
};

export const loadImage = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Could not render the chart image"));
    img.src = src;
  });

/** An SVG string as an image to draw on a canvas. */
export const svgImage = (svg) => loadImage(`data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`);

/**
 * Download the chart inside `container` as a PNG.
 *
 * title:    the chart's name, drawn at the top and used for the filename
 * subtitle: usually the date range the chart covers
 * legend:   [{label, color}] drawn under the chart, so the image is readable
 *           without the page around it
 */
export const downloadChartPng = async ({ container, title, subtitle, legend = [], scale = 2 }) => {
  const chart = largestSvg(container);
  if (!chart) throw new Error("There is no chart to download yet");
  const { svg, width, height } = chart;

  const clone = svg.cloneNode(true);
  clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
  clone.setAttribute("width", String(width));
  clone.setAttribute("height", String(height));
  inlineTextStyles(svg, clone);
  const image = await svgImage(new XMLSerializer().serializeToString(clone));
  await downloadFramedPng({ image, width, height, title, subtitle, legend, scale });
};

/**
 * Download `image` (anything a canvas can draw, `width` x `height` CSS
 * pixels) as a PNG framed like every chart download: title and subtitle
 * above, legend below, and an optional `credit` line (a map's attribution).
 */
export const downloadFramedPng = async ({ image, width, height, title, subtitle, legend = [], credit, scale = 2 }) => {
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  const contentWidth = Math.max(width, 480);
  ctx.font = `13px ${FONT}`;
  const { placed, rows } = layoutLegend(legend, contentWidth, (text) => ctx.measureText(text).width);

  const titleHeight = title ? 30 : 0;
  const subtitleHeight = subtitle ? 22 : 0;
  const legendHeight = rows ? rows * LEGEND_ROW + 12 : 0;
  const creditHeight = credit ? 18 : 0;
  const totalWidth = contentWidth + PAD * 2;
  const totalHeight = PAD + titleHeight + subtitleHeight + height + legendHeight + creditHeight + PAD;

  canvas.width = totalWidth * scale;
  canvas.height = totalHeight * scale;
  ctx.scale(scale, scale);
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, totalWidth, totalHeight);

  let y = PAD;
  ctx.textBaseline = "top";
  if (title) {
    ctx.fillStyle = CHART_INK.primary;
    ctx.font = `600 18px ${FONT}`;
    ctx.fillText(title, PAD, y);
    y += titleHeight;
  }
  if (subtitle) {
    ctx.fillStyle = CHART_INK.secondary;
    ctx.font = `13px ${FONT}`;
    ctx.fillText(subtitle, PAD, y);
    y += subtitleHeight;
  }
  ctx.drawImage(image, PAD, y, width, height);
  y += height + 12;

  ctx.font = `13px ${FONT}`;
  ctx.textBaseline = "middle";
  placed.forEach(({ label, color, x, row }) => {
    const rowY = y + row * LEGEND_ROW;
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.roundRect(PAD + x, rowY, SWATCH, SWATCH, 2);
    ctx.fill();
    ctx.fillStyle = CHART_INK.primary;
    ctx.fillText(label, PAD + x + SWATCH + 6, rowY + SWATCH / 2);
  });

  if (credit) {
    ctx.font = `11px ${FONT}`;
    ctx.fillStyle = CHART_INK.muted;
    ctx.textBaseline = "bottom";
    ctx.textAlign = "right";
    ctx.fillText(credit, PAD + contentWidth, totalHeight - PAD + 4);
    ctx.textAlign = "left";
  }

  const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
  if (!blob) throw new Error("The image could not be made");
  fileDownload(blob, exportFilename(title, "png"));
};
